import {
    createLogscordPublicHydrationProtocol,
    type LogscordPublicRenderState,
} from "@logscord/public-site-config";
import {
    inject,
    ref,
    type InjectionKey,
    type Ref,
} from "vue";


//////////////////////////////////////////////////
//  APP STATE
//////////////////////////////////////////////////


export type AppStateSnapshot = LogscordPublicRenderState;
export type AppStateInput = {
    publicSite?  : Partial<AppStateSnapshot["publicSite"]>;
    application?: Partial<AppStateSnapshot["application"]>;
};

export type AppStateStore = {
    locale          : Ref<string>;
    localeLocked    : Ref<boolean>;
    setLocale       : (locale: string) => void;
    setLocaleLocked : (locked: boolean) => void;
    snapshot        : () => AppStateSnapshot;
};

declare global {
    interface Window {
        __LOGSCORD_APP_STATE__?: Partial<AppStateSnapshot>;
    }
}

const DefaultAppState: AppStateSnapshot = {
    publicSite: {
        protocolVersion : 1,
        buildId         : __LOGSCORD_PUBLIC_BUILD_ID__,
    },
    application: {
        locale       : "en-US",
        localeLocked : false,
    },
};
const HydrationProtocol = createLogscordPublicHydrationProtocol(__LOGSCORD_PUBLIC_BUILD_ID__);

export const AppStateInjectionKey: InjectionKey<AppStateStore> = Symbol("logscord-app-state");

let BrowserAppState: AppStateStore | null = null;

export function isCompatibleAppState(
    state: unknown,
): state is AppStateSnapshot {
    return HydrationProtocol.isCompatible(state);
}

export function createAppState(initialState: AppStateInput = {}): AppStateStore {
    const applicationState = initialState.application;
    const locale = ref(applicationState?.locale || DefaultAppState.application.locale);
    const localeLocked = ref(
        applicationState?.localeLocked ?? DefaultAppState.application.localeLocked,
    );
    return {
        locale,
        localeLocked,

        setLocale(nextLocale: string) {
            locale.value = nextLocale;
        },

        setLocaleLocked(locked: boolean) {
            localeLocked.value = locked;
        },

        snapshot() {
            return HydrationProtocol.create({
                locale       : locale.value,
                localeLocked : localeLocked.value,
            });
        },
    };
}

export function createBrowserAppState() {
    if (!BrowserAppState) {
        const browserState: unknown = window.__LOGSCORD_APP_STATE__;

        BrowserAppState = createAppState(
            isCompatibleAppState(browserState)
                ? browserState
                : DefaultAppState,
        );
    }

    return BrowserAppState;
}

export function useAppState() {
    const injectedAppState = inject(AppStateInjectionKey, null);

    if (injectedAppState)
        return injectedAppState;

    if (typeof window !== "undefined")
        return createBrowserAppState();

    return createAppState(DefaultAppState);
}
